import { wrapReducer } from "@client/content/lib/helper";
import { type FailureActionData } from "@client/lib/actions";
import { type ReduxAction } from "@client/types/ReduxAction";
import {
  Actions,
  type AdjustOrderInfoBeginActionData,
  type AdjustOrderInfoSuccessActionData,
  type InitSuccessActionData,
  type PlaceOrderSuccessActionData,
  type SetOrderActionData,
  type SetShouldAdjustOrderActionData,
  type SetVariantIdActionData
} from "./actions";
import { type Checkout } from "./types";

export const EMPTY_CART: Checkout.Cart = {
  canApplyDiscountCodes: false,
  canApplyVoucherCodes: false,
  coupons: [],
  discountAmount: 0,
  dueAmount: 0,
  hasSubscription: false,
  paidAmount: 0,
  productOptions: [],
  items: [],
  offerItem: null,
  paymentMethods: [],
  requiresShipping: false,
  shippingAmount: 0,
  shippingRules: [],
  subtotalAmount: 0,
  surcharges: [],
  taxAmount: 0,
  taxTotalAmount: 0,
  totalAmount: 0,
  variantItems: [],
  voucherAmount: null
};

const reduceCart = (cart: Checkout.Cart, apiCart: Checkout.Api.Cart): Checkout.Cart => ({
  ...apiCart,
  paymentMethods: apiCart.paymentMethods ?? cart.paymentMethods,
  shippingRules: apiCart.shippingRules ?? cart.shippingRules
});

const reducer = (state: Checkout.State, action: ReduxAction<any>): Checkout.State => {
  switch (action.type) {
    case Actions.ClearFailureReason: {
      return {
        ...state,
        failureReason: null
      };
    }

    case Actions.InitBegin: {
      return {
        ...state,
        status: "Loading"
      };
    }
    case Actions.InitFailure: {
      return {
        ...state,
        status: "Failed"
      };
    }
    case Actions.InitSuccess: {
      const {
        orderId,
        consentFieldIds,
        confirmUrl,
        defaultBillingAddressId,
        defaultShippingAddressId,
        eventDateLocationId,
        eventId,
        excludeFields,
        currencyCode,
        cart,
        productVariantId,
        shippingRuleId,
        userAddresses,
        userAccounts
      } = action.data as InitSuccessActionData;
      return {
        ...state,
        orderId,
        confirmUrl,
        consentFieldIds,
        defaultBillingAddressId,
        defaultShippingAddressId,
        event:
          eventDateLocationId || eventId
            ? {
                eventDateLocationId,
                eventId
              }
            : null,
        excludeFields,
        currencyCode,
        cart: reduceCart(state.cart, cart),
        productVariantId,
        shippingRuleId,
        status: "Ready",
        userAccounts,
        userAddresses
      };
    }

    case Actions.PlaceOrderBegin: {
      return {
        ...state,
        error: null,
        status: "Saving"
      };
    }
    case Actions.PlaceOrderFailure: {
      const { error } = action.data as FailureActionData;
      return {
        ...state,
        error,
        status: "Ready"
      };
    }
    case Actions.PlaceOrderSuccess: {
      const { failureReason } = action.data as PlaceOrderSuccessActionData;
      return {
        ...state,
        failureReason,
        status: failureReason ? "Ready" : state.status
      };
    }

    case Actions.AdjustOrderBegin: {
      const { coupons } = action.data as AdjustOrderInfoBeginActionData;
      return {
        ...state,
        error: null,
        invalidCoupons: coupons?.length ? [] : state.invalidCoupons,
        shouldAdjust: false,
        status: "AdjustingOrderInfo"
      };
    }
    case Actions.AdjustOrderFailure: {
      const { error } = action.data as FailureActionData;
      return {
        ...state,
        error,
        status: "Ready"
      };
    }
    case Actions.AdjustOrderSuccess: {
      const { coupons } = action.params as AdjustOrderInfoBeginActionData;
      const { cart, invalidCoupons, currencyCode } = action.data as AdjustOrderInfoSuccessActionData;
      return {
        ...state,
        cart: {
          ...cart,
          paymentMethods: cart.paymentMethods ?? state.cart.paymentMethods,
          shippingRules: cart.shippingRules ?? state.cart.shippingRules
        },
        invalidCoupons: coupons?.length ? invalidCoupons : state.invalidCoupons,
        currencyCode,
        status: "Ready"
      };
    }

    case Actions.SetShouldAdjustOrder: {
      const { id, shouldAdjust, ...adjustOrderInfo } = action.data as SetShouldAdjustOrderActionData;
      return {
        ...state,
        adjustOrderInfo,
        shouldAdjust
      };
    }

    case Actions.SetOrder: {
      const { order } = action.data as SetOrderActionData;
      return {
        ...state,
        order
      };
    }

    case Actions.SetVariantId: {
      const { variantId } = action.data as SetVariantIdActionData;
      return {
        ...state,
        productVariantId: variantId
      };
    }

    default:
      return state;
  }
};

export const defaultState: Checkout.State = {
  orderId: null,
  adjustOrderInfo: {
    coupons: null,
    billingEmail: null,
    eventDateLocationId: null,
    eventId: null,
    includeOffer: false,
    billingCountryCode: null,
    currencyCode: null,
    billingSubdivisionCode: null,
    offerProductVariantId: null,
    productId: null,
    productVariantId: null,
    shippingCity: null,
    shippingCountryCode: null,
    shippingSubdivisionCode: null,
    pargoPickupPoint: null,
    shippingRuleId: null
  },
  consentFieldIds: [],
  defaultBillingAddressId: null,
  defaultShippingAddressId: null,
  error: null,
  event: null,
  excludeFields: [],
  id: "",
  invalidCoupons: [],
  currencyCode: "",
  status: "None",
  confirmUrl: "",
  failureReason: null,
  productVariantId: null,
  cart: EMPTY_CART,
  order: null,
  shouldAdjust: false,
  shippingRuleId: null,
  userAddresses: [],
  userAccounts: []
};
export const previewState: Checkout.State = {
  ...defaultState,
  status: "Ready"
};
export default wrapReducer({ defaultState, reducer, previewState });
